import { ReactComponent as RightArrow } from '@assets/icons/32x32/icPlayer-next-32.svg';
import { ReactComponent as LeftArrow } from '@assets/icons/32x32/icPlayer-previous-32.svg';
import { For, HStack, Icon, Slider as ChakraSlider, SliderValueChangeDetails } from '@chakra-ui/react';
import { debounce } from 'lodash';
import { forwardRef, ReactNode, useCallback, useEffect, useState } from 'react';

function SliderThumbs(props: Readonly<{ value?: number[] }>) {
  const { value } = props;
  return (
    <For each={value}>
      {(_, index) => (
        <ChakraSlider.Thumb key={index} index={index} borderWidth="6px" borderColor="blue.600">
          <ChakraSlider.HiddenInput />
        </ChakraSlider.Thumb>
      )}
    </For>
  );
}

interface SliderMarksProps {
  marks?: Array<number | { value: number; label: ReactNode }>;
}

const SliderMarks = forwardRef<HTMLDivElement, SliderMarksProps>(function SliderMarks(props, ref) {
  const { marks } = props;
  if (!marks?.length) return null;

  return (
    <ChakraSlider.MarkerGroup ref={ref}>
      {marks.map((mark) => {
        const value = typeof mark === 'number' ? mark : mark.value;
        const label = typeof mark === 'number' ? undefined : mark.label;
        return (
          <ChakraSlider.Marker key={value} value={value}>
            <ChakraSlider.MarkerIndicator />
            {label}
          </ChakraSlider.Marker>
        );
      })}
    </ChakraSlider.MarkerGroup>
  );
});

export interface SliderProps extends Omit<ChakraSlider.RootProps, 'onValueChange'> {
  marks?: Array<number | { value: number; label: ReactNode }>;
  label?: ReactNode;
  showValue?: boolean;
  delay?: number;
  showArrows?: boolean;
  onValueChange: (value: SliderValueChangeDetails) => void;
}

export const Slider = forwardRef<HTMLDivElement, SliderProps>(function Slider(props, ref) {
  const {
    marks: marksProp,
    label,
    showValue,
    delay = 300,
    value,
    showArrows = false,
    onValueChange,
    min = 0,
    max = 100,
    step = 1,
    ...rest
  } = props;
  const [innerValues, setInnerValues] = useState<number[] | undefined>(props.defaultValue ?? value);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChange = useCallback(
    debounce((value) => {
      onValueChange(value);
    }, delay),
    [],
  );

  useEffect(() => setInnerValues(value), [value]);

  const handleChange = (value: SliderValueChangeDetails) => {
    setInnerValues(value.value);
    debouncedChange(value);
  };

  const marks = marksProp?.map((mark) => {
    return typeof mark === 'number' ? { value: mark, label: undefined } : mark;
  });

  const hasMarkLabel = !!marks?.some((mark) => mark.label !== undefined);

  const leftArrowDisabled = innerValues && innerValues[0] <= min;
  const rightArrowDisabled = innerValues && innerValues[innerValues.length - 1] >= max;
  const isHorizontal = props.orientation !== 'vertical';

  return (
    <HStack gap={1} width={isHorizontal ? '100%' : undefined}>
      {isHorizontal && showArrows && (
        <Icon
          as={LeftArrow}
          width="32px"
          height="32px"
          color="neutral.300"
          cursor={leftArrowDisabled ? 'default' : 'pointer'}
          _hover={{ color: leftArrowDisabled ? 'neutral.300' : 'skyBlue' }}
          onClick={() =>
            !leftArrowDisabled && innerValues && handleChange({ value: [Math.max(innerValues[0] - step, min)] })
          }
        />
      )}
      <ChakraSlider.Root
        ref={ref}
        thumbAlignment="center"
        onValueChange={handleChange}
        value={innerValues}
        min={min}
        max={max}
        step={step}
        {...rest}
      >
        {label && !showValue && <ChakraSlider.Label>{label}</ChakraSlider.Label>}
        {label && showValue && (
          <HStack justify="space-between">
            <ChakraSlider.Label>{label}</ChakraSlider.Label>
            <ChakraSlider.ValueText />
          </HStack>
        )}
        <ChakraSlider.Control data-has-mark-label={hasMarkLabel || undefined}>
          <ChakraSlider.Track>
            <ChakraSlider.Range />
          </ChakraSlider.Track>
          <SliderThumbs value={innerValues} />
          <SliderMarks marks={marks} />
        </ChakraSlider.Control>
      </ChakraSlider.Root>
      {isHorizontal && showArrows && (
        <Icon
          as={RightArrow}
          width="32px"
          height="32px"
          color="neutral.300"
          cursor={rightArrowDisabled ? 'default' : 'pointer'}
          _hover={{ color: rightArrowDisabled ? 'neutral.300' : 'skyBlue' }}
          onClick={() =>
            !rightArrowDisabled &&
            innerValues &&
            handleChange({ value: [Math.min(innerValues[innerValues.length - 1] + step, max)] })
          }
        />
      )}
    </HStack>
  );
});
